import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Upload,
  FolderOpen,
  Clock,
  TrendingUp,
  FileText,
  Plus,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from 'lucide-react';

export function DashboardPage() {
  const { api, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, projectsRes] = await Promise.all([
          api.get('/stats'),
          api.get('/projects'),
        ]);
        setStats(statsRes.data);
        setProjects(projectsRes.data.projects || []);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="badge-go">
            <CheckCircle2 size={12} className="mr-1" /> Analysé
          </span>
        );
      case 'processing':
        return (
          <span className="badge-vigilance">
            <Loader2 size={12} className="mr-1 animate-spin" /> En cours
          </span>
        );
      case 'failed':
        return (
          <span className="badge-nogo">
            <AlertCircle size={12} className="mr-1" /> Erreur
          </span>
        );
      default:
        return (
          <span className="badge-neutral">
            <Clock size={12} className="mr-1" /> En attente
          </span>
        );
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            Bonjour, {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="text-slate-600 mt-1">
            Bienvenue sur votre espace DCE Analyzer
          </p>
        </div>
        <Link to="/upload">
          <Button className="gap-2" data-testid="dashboard-upload-btn">
            <Plus size={20} />
            Analyser un DCE
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="border-l-4 border-l-primary">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">
              Projets analysés
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold text-slate-900">
                {stats?.total_projects || 0}
              </span>
              <FolderOpen className="w-8 h-8 text-primary/20" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-dce-go">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">
              Analyses terminées
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold text-slate-900">
                {stats?.completed_projects || 0}
              </span>
              <CheckCircle2 className="w-8 h-8 text-dce-go/20" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-dce-vigilance">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">
              En attente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold text-slate-900">
                {stats?.pending_projects || 0}
              </span>
              <Clock className="w-8 h-8 text-dce-vigilance/20" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-slate-400">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">
              Analyses ce mois
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-3xl font-bold text-slate-900">
                  {stats?.analyses_this_month || 0}
                </span>
                {user?.plan === 'novice' && (
                  <span className="text-slate-400 text-sm"> / 5</span>
                )}
              </div>
              <TrendingUp className="w-8 h-8 text-slate-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-primary to-primary-hover text-white">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading text-xl font-bold mb-2">
                  Analyser un nouveau DCE
                </h3>
                <p className="text-white/80 text-sm mb-4">
                  Uploadez votre document PDF et obtenez une analyse complète en quelques minutes.
                </p>
                <Link to="/upload">
                  <Button
                    variant="secondary"
                    className="bg-white text-primary hover:bg-white/90 gap-2"
                    data-testid="quick-upload-btn"
                  >
                    <Upload size={18} />
                    Uploader un fichier
                  </Button>
                </Link>
              </div>
              <FileText className="w-16 h-16 text-white/20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                  Plan {user?.plan?.charAt(0).toUpperCase() + user?.plan?.slice(1)}
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  {user?.plan === 'novice'
                    ? 'Passez au plan Pro pour des analyses illimitées et plus de fonctionnalités.'
                    : 'Vous bénéficiez d\'analyses illimitées et de toutes les fonctionnalités.'}
                </p>
                {user?.plan === 'novice' && (
                  <Button variant="outline" className="gap-2" data-testid="upgrade-btn">
                    Passer au Pro
                    <ArrowRight size={18} />
                  </Button>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Ce mois</p>
                <p className="text-2xl font-bold text-slate-900">
                  {stats?.analyses_this_month || 0}
                  {user?.plan === 'novice' && <span className="text-slate-400"> / 5</span>}
                </p>
                <p className="text-xs text-slate-500">analyses</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Projects */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-xl">Projets récents</CardTitle>
          <Link to="/projects">
            <Button variant="ghost" size="sm" className="gap-1" data-testid="view-all-projects">
              Voir tout
              <ArrowRight size={16} />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {projects.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">Aucun projet pour le moment</p>
              <Link to="/upload">
                <Button variant="outline" data-testid="empty-upload-btn">
                  Analyser votre premier DCE
                </Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {projects.slice(0, 5).map((project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="flex items-center gap-4 py-4 hover:bg-slate-50 -mx-6 px-6 transition-colors"
                  data-testid={`project-${project.id}`}
                >
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{project.title}</p>
                    <p className="text-sm text-slate-500">{formatDate(project.created_at)}</p>
                  </div>
                  {getStatusBadge(project.status)}
                  <ArrowRight className="w-5 h-5 text-slate-400" />
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}
